/**
 * Payment API client (Sprint 5 B6 + giỏ hàng Sprint 34).
 *
 * Endpoints:
 *   - POST /api/payment/courses/<slug>/checkout/   — init order 1 môn, trả payment_link
 *   - POST /api/payment/cart/checkout/             — giỏ hàng nhiều môn, 1 order duy nhất
 *   - GET  /api/payment/me/orders/?status=        — list my orders
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "./client";
import type { MissingPrerequisite } from "./enrollment";

// ─── Types ────────────────────────────────────────────────────────────────

export type OrderStatus = "pending" | "paid" | "cancelled" | "refunded" | "failed";

export type PaymentProvider = "mock" | "payos" | "stripe";

export interface OrderCourse {
  id: number;
  code: string;
  slug: string;
  name: string;
  cover_image: string | null;
}

/** 1 dòng môn học trong order (Sprint 34 — giỏ hàng nhiều môn). */
export interface OrderItem {
  id: number;
  course: OrderCourse;
  term: { id: number; name: string; starts_at: string; ends_at: string } | null;
  amount_cents: number;
  amount_display: string;
}

export interface Order {
  id: number;
  status: OrderStatus;
  amount_cents: number;
  amount_display: string;
  currency: string;
  payment_provider: PaymentProvider;
  payment_link: string;
  created_at: string;
  paid_at: string | null;
  cancelled_at: string | null;
  // Sprint 34 — snapshot order 1 môn; null với order nhiều môn (giỏ hàng),
  // dùng `items` thay (luôn có, kể cả order 1 môn — 1 dòng).
  course: OrderCourse | null;
  items: OrderItem[];
}

/** Response variant — free enrollment (price=0) */
export interface FreeEnrollmentResponse {
  kind: "free_enrollment";
  course_slug: string;
  enrollment_id: number;
  status: string;
}

export type CheckoutResponse = Order | FreeEnrollmentResponse;

export function isFreeEnrollment(r: CheckoutResponse): r is FreeEnrollmentResponse {
  return (r as FreeEnrollmentResponse).kind === "free_enrollment";
}

export interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ─── Giỏ hàng nhiều môn (Sprint 34) ─────────────────────────────────────────

export type CartCheckoutSkipStatus =
  | "not_found"
  | "tenant_mismatch"
  | "blocked_prerequisites"
  | "pricing_not_configured"
  | "already_enrolled";

/** 1 môn bị loại khỏi giỏ hàng khi checkout — dòng trong `skipped_items`. */
export interface CartCheckoutSkippedItem {
  course_slug: string;
  status: CartCheckoutSkipStatus;
  detail: string;
  missing_prerequisites?: MissingPrerequisite[];
}

export interface CartCheckoutResponse extends Order {
  skipped_items: CartCheckoutSkippedItem[];
}

// ─── Format helpers ────────────────────────────────────────────────────────

/** 500000 → "500.000 ₫". Mirror backend Order.amount_display logic. */
export function formatAmountVND(cents: number): string {
  return cents.toLocaleString("vi-VN").replace(/,/g, ".") + " ₫";
}

// ─── Hooks ────────────────────────────────────────────────────────────────

/** POST /api/payment/courses/<slug>/checkout/ — init order. */
export function useCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (courseSlug: string) => {
      const resp = await apiClient.post<CheckoutResponse>(
        `/payment/courses/${courseSlug}/checkout/`,
      );
      return resp.data;
    },
    onSuccess: () => {
      // Invalidate cả enrollment (free → tạo enrollment) + payment (paid order)
      qc.invalidateQueries({ queryKey: ["enrollment"] });
      qc.invalidateQueries({ queryKey: ["payment"] });
    },
  });
}

/**
 * POST /api/payment/cart/checkout/ — giỏ hàng nhiều môn (Sprint 34).
 *
 * Gộp mọi môn (free lẫn có phí) vào 1 Order duy nhất. `Idempotency-Key`
 * (UUID gen mỗi lần bấm "Đăng ký") chống double-submit tạo 2 order khi user
 * bấm 2 lần / F5 giữa chừng — server trả lại order cũ nếu key trùng.
 *
 * `order.status === "paid"` (tổng 0đ, toàn môn free) → đã enroll hết ngay,
 * không có `payment_link`. Ngược lại FE redirect `payment_link` để thanh toán.
 */
export function useCartCheckout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (courseSlugs: string[]) => {
      const idempotencyKey =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `fallback-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const resp = await apiClient.post<CartCheckoutResponse>(
        "/payment/cart/checkout/",
        { course_slugs: courseSlugs },
        { headers: { "Idempotency-Key": idempotencyKey } },
      );
      return resp.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["enrollment"] });
      qc.invalidateQueries({ queryKey: ["payment"] });
    },
  });
}

/** GET /api/payment/me/orders/?status=<value> — list my orders. */
export function useMyOrders(statusFilter?: OrderStatus) {
  return useQuery({
    queryKey: ["payment", "me", "orders", statusFilter ?? "all"],
    queryFn: async () => {
      const params = statusFilter ? { status: statusFilter } : {};
      const resp = await apiClient.get<Paginated<Order>>(
        "/payment/me/orders/",
        { params },
      );
      return resp.data.results;
    },
    staleTime: 30_000,
  });
}
